/**
 * diwaliCrackers.js
 * 
 * High-performance, lightweight Diwali Crackers & Fireworks Canvas Engine.
 * Simulates traditional Indian Diwali crackers:
 * - Sky Rockets (Skyshots with fiery trails & radial sparkling bursts)
 * - Anar (Flowerpot sparkling golden fountains)
 * - Phooljhadi (Glittering sparklers & crackles)
 * 
 * Non-blocking, zero external dependencies, 60fps hardware-accelerated.
 */

import { isDiwaliDay } from "./festivalUtils";

const DIWALI_COLORS = [
  "#f59e0b", // Warm Gold
  "#fbbf24", // Sun Amber
  "#fde047", // Brilliant Yellow
  "#ef4444", // Festive Crimson Red
  "#f43f5e", // Rose Pink
  "#10b981", // Emerald Green
  "#34d399", // Mint Green
  "#06b6d4", // Electric Cyan
  "#38bdf8", // Sky Blue
  "#8b5cf6", // Royal Purple
  "#d946ef", // Vibrant Magenta
  "#ffffff", // Core White Spark
];

class CrackerParticle {
  constructor(x, y, color, speed, angle, size = 2.4, decay = null) {
    this.x = x;
    this.y = y;
    this.color = color;
    this.vx = Math.cos(angle) * speed;
    this.vy = Math.sin(angle) * speed;
    this.alpha = 1;
    this.decay = decay || (Math.random() * 0.016 + 0.012);
    this.gravity = 0.075;
    this.friction = 0.965;
    this.size = size;
    this.trail = [];
    this.maxTrail = Math.floor(Math.random() * 3 + 3);
  }

  update() {
    this.trail.push({ x: this.x, y: this.y, alpha: this.alpha });
    if (this.trail.length > this.maxTrail) this.trail.shift();

    this.vx *= this.friction;
    this.vy *= this.friction;
    this.vy += this.gravity;
    this.x += this.vx;
    this.y += this.vy;
    this.alpha -= this.decay;
  }

  draw(ctx) {
    // Draw sparkling trail
    for (let i = 0; i < this.trail.length; i++) {
      const pt = this.trail[i];
      const r = this.size * ((i + 1) / (this.trail.length + 1));
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, r, 0, Math.PI * 2);
      ctx.fillStyle = this.color;
      ctx.globalAlpha = Math.max(0, pt.alpha * 0.45);
      ctx.fill();
    }

    // Draw main particle head
    ctx.beginPath();
    ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
    ctx.fillStyle = this.color;
    ctx.globalAlpha = Math.max(0, this.alpha);
    ctx.fill();
  }
}

class Rocket {
  constructor(startX, startY, targetX, targetY, primaryColor) {
    this.x = startX;
    this.y = startY;
    this.targetX = targetX;
    this.targetY = targetY;
    this.color = primaryColor;
    const dx = targetX - startX;
    const dy = targetY - startY;
    const dist = Math.hypot(dx, dy);
    const speed = Math.random() * 3 + 12;
    this.vx = (dx / dist) * speed;
    this.vy = (dy / dist) * speed;
    this.exploded = false;
    this.trail = [];
  }

  update() {
    this.trail.push({ x: this.x, y: this.y });
    if (this.trail.length > 7) this.trail.shift();

    this.x += this.vx;
    this.y += this.vy;
    this.vy += 0.05; // slight deceleration

    if (this.vy >= -1.5 || this.y <= this.targetY) {
      this.exploded = true;
    }
  }

  draw(ctx) {
    // Fiery rocket spark tail
    for (let i = 0; i < this.trail.length; i++) {
      const pt = this.trail[i];
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 1.8 * (i / this.trail.length), 0, Math.PI * 2);
      ctx.fillStyle = "#fbbf24";
      ctx.globalAlpha = 0.5 * (i / this.trail.length);
      ctx.fill();
    }

    // Rocket head
    ctx.beginPath();
    ctx.arc(this.x, this.y, 2.8, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.globalAlpha = 1;
    ctx.fill();
  }
}

let activeCanvas = null;
let activeCtx = null;
let animFrameId = null;
let activeRockets = [];
let activeParticles = [];
let isRunning = false;

function setupCanvas() {
  if (activeCanvas && document.body.contains(activeCanvas)) {
    return { canvas: activeCanvas, ctx: activeCtx };
  }

  const canvas = document.createElement("canvas");
  canvas.id = "oxy-diwali-crackers-canvas";
  canvas.style.position = "fixed";
  canvas.style.top = "0";
  canvas.style.left = "0";
  canvas.style.width = "100vw";
  canvas.style.height = "100vh";
  canvas.style.pointerEvents = "none";
  canvas.style.zIndex = "999999";

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = Math.floor(window.innerWidth * dpr);
  canvas.height = Math.floor(window.innerHeight * dpr);

  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);

  document.body.appendChild(canvas);

  activeCanvas = canvas;
  activeCtx = ctx;

  const handleResize = () => {
    if (!activeCanvas) return;
    const rDpr = Math.min(window.devicePixelRatio || 1, 2);
    activeCanvas.width = Math.floor(window.innerWidth * rDpr);
    activeCanvas.height = Math.floor(window.innerHeight * rDpr);
    if (activeCtx) activeCtx.scale(rDpr, rDpr);
  };

  window.addEventListener("resize", handleResize, { passive: true });

  return { canvas, ctx };
}

function spawnBurst(x, y, primaryColor, count = 65) {
  const chosenColor = primaryColor || DIWALI_COLORS[Math.floor(Math.random() * DIWALI_COLORS.length)];
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = Math.random() * 6.5 + 1.2;
    const color = Math.random() > 0.35 ? chosenColor : DIWALI_COLORS[Math.floor(Math.random() * DIWALI_COLORS.length)];
    const size = Math.random() > 0.85 ? 3.2 : Math.random() * 1.8 + 1.4;
    activeParticles.push(new CrackerParticle(x, y, color, speed, angle, size));
  }
}

function spawnAnarFountain(x, y) {
  // Shimmering golden fountain sparks shooting upwards
  for (let i = 0; i < 30; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.8;
    const speed = Math.random() * 7 + 4;
    const color = Math.random() > 0.5 ? "#fbbf24" : Math.random() > 0.5 ? "#f59e0b" : "#ffffff";
    activeParticles.push(new CrackerParticle(x, y, color, speed, angle, 2.2, Math.random() * 0.02 + 0.02));
  }
}

/**
 * Triggers a festive Diwali crackers / fireworks celebration on the screen.
 * 
 * @param {Object} options
 * @param {number} options.duration - Duration in ms for launching rockets (default: 4200ms)
 * @param {number} options.originX - Optional burst center X (for targeted clicks)
 * @param {number} options.originY - Optional burst center Y (for targeted clicks)
 * @param {boolean} options.anar - Whether to include Anar (ground flowerpot fountains)
 * @param {boolean} options.force - If true, ignores the isDiwaliDay check (e.g. for testing)
 */
export const triggerDiwaliCrackers = (options = {}) => {
  if (typeof window === "undefined") return;

  const {
    duration = 4200,
    originX = null,
    originY = null,
    anar = true,
    force = false,
  } = options;

  // Only trigger on Diwali days unless explicitly forced
  if (!force && !isDiwaliDay()) {
    return;
  }

  const { ctx } = setupCanvas();

  // If specific point was tapped/clicked (e.g. Header Doodle), immediately spawn localized bursts
  if (originX !== null && originY !== null) {
    spawnBurst(originX, originY, "#f59e0b", 70);
    spawnBurst(originX + (Math.random() - 0.5) * 60, originY + (Math.random() - 0.5) * 40, "#10b981", 50);
    spawnBurst(originX + (Math.random() - 0.5) * 80, originY + (Math.random() - 0.5) * 50, "#ef4444", 50);
  }

  // Schedule volley of festive rockets across screen width
  const w = window.innerWidth;
  const h = window.innerHeight;

  const startTime = performance.now();
  const rocketInterval = setInterval(() => {
    if (performance.now() - startTime > duration) {
      clearInterval(rocketInterval);
      return;
    }

    // Launch 1-2 rockets from bottom
    const count = Math.random() > 0.5 ? 2 : 1;
    for (let c = 0; c < count; c++) {
      const startX = Math.random() * (w * 0.8) + (w * 0.1);
      const targetX = startX + (Math.random() - 0.5) * 120;
      const targetY = Math.random() * (h * 0.45) + (h * 0.12);
      const color = DIWALI_COLORS[Math.floor(Math.random() * DIWALI_COLORS.length)];
      activeRockets.push(new Rocket(startX, h + 10, targetX, targetY, color));
    }

    // Occasionally spawn anar fountain sparks near corners
    if (anar && Math.random() > 0.6) {
      spawnAnarFountain(w * 0.08, h);
      spawnAnarFountain(w * 0.92, h);
    }
  }, 320);

  // Initial immediate rocket
  activeRockets.push(new Rocket(w * 0.3, h + 10, w * 0.32, h * 0.25, "#fbbf24"));
  activeRockets.push(new Rocket(w * 0.7, h + 10, w * 0.68, h * 0.22, "#10b981"));

  if (!isRunning) {
    isRunning = true;
    const loop = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      // Update & draw rockets
      for (let i = activeRockets.length - 1; i >= 0; i--) {
        const r = activeRockets[i];
        r.update();
        r.draw(ctx);
        if (r.exploded) {
          spawnBurst(r.x, r.y, r.color, 65);
          activeRockets.splice(i, 1);
        }
      }

      // Update & draw particles
      for (let i = activeParticles.length - 1; i >= 0; i--) {
        const p = activeParticles[i];
        p.update();
        if (p.alpha <= 0) {
          activeParticles.splice(i, 1);
        } else {
          p.draw(ctx);
        }
      }

      // If active rockets or particles remain, continue loop
      if (activeRockets.length > 0 || activeParticles.length > 0) {
        animFrameId = requestAnimationFrame(loop);
      } else {
        isRunning = false;
        ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
        if (activeCanvas && activeCanvas.parentNode) {
          activeCanvas.parentNode.removeChild(activeCanvas);
          activeCanvas = null;
          activeCtx = null;
        }
      }
    };
    animFrameId = requestAnimationFrame(loop);
  }
};

// Global event listener for easy decoupling
if (typeof window !== "undefined") {
  window.addEventListener("burst-diwali-crackers", (e) => {
    triggerDiwaliCrackers(e.detail || {});
  });
}

export default triggerDiwaliCrackers;
